import {
  Box,
  Button,
  Card,
  Group,
  MantineProvider,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconAlertCircle, IconCalendarOff } from "@tabler/icons-react";
import { ReactNode, useEffect, useState } from "react";
import { useGetWebinars } from "../../../../api/webinar/useWebinar";
import { IWebinar } from "../../../../models/interfaces/IWebinar.interface";
import withClientNumber from "../../hoc/ClientNumber";
import { theme } from "../../Theme";
import { WebinarCard } from "./WebinarCard";
import { JoinWebinarModal, ShareWebinarModal } from "./WebinarModals";
import { WebinarSection } from "./WebinarSection";

const DEFAULT_FRONTEND_URL = "https://f116.firmli.com";

function WebinarCardSkeleton() {
  return (
    <Card withBorder radius="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Skeleton h={20} w={90} radius="sm" />
          <Skeleton h={20} w="90%" />
          <Skeleton h={20} w="60%" />
        </Stack>
        <Skeleton w={80} h={100} radius="md" />
      </Group>
      <Stack gap="md" mt="lg">
        <Skeleton h={14} w="70%" />
        <Skeleton h={14} w="85%" />
        <Skeleton h={14} w="50%" />
      </Stack>
    </Card>
  );
}

function StatusMessage({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <Card withBorder radius="lg" py={48}>
      <Stack align="center" ta="center" gap="xs">
        {icon}
        <Text fw={600} mt="xs">
          {title}
        </Text>
        <Text fz="sm" c="dimmed" maw={360}>
          {description}
        </Text>
        {children}
      </Stack>
    </Card>
  );
}

const WebinarLayout = () => {
  const {
    data: webinars,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetWebinars({ enabled: true });
  const [now, setNow] = useState(() => Date.now());
  const [activeModal, setActiveModal] = useState<"share" | "join" | null>(
    null
  );
  const [selectedEvent, setSelectedEvent] = useState<IWebinar | null>(null);

  // Keep "Live now" badges and Join buttons current while the widget stays open.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const ongoing = webinars?.ongoing ?? [];
  const upcoming = webinars?.upcoming ?? [];

  const openModal = (type: "share" | "join", event: IWebinar) => {
    setSelectedEvent(event);
    setActiveModal(type);
  };

  const renderCards = (events: IWebinar[]) =>
    events.map((event) => (
      <WebinarCard
        key={event.identifier}
        event={event}
        now={now}
        onShare={(e) => openModal("share", e)}
        onJoin={(e) => openModal("join", e)}
      />
    ));

  let content: ReactNode;
  if (isLoading) {
    content = (
      <Stack gap="sm">
        <Skeleton h={26} w={140} />
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <WebinarCardSkeleton />
          <Box visibleFrom="sm">
            <WebinarCardSkeleton />
          </Box>
          <Box visibleFrom="lg">
            <WebinarCardSkeleton />
          </Box>
        </SimpleGrid>
      </Stack>
    );
  } else if (isError) {
    content = (
      <StatusMessage
        icon={
          <ThemeIcon color="red" variant="light" radius="xl" size={48}>
            <IconAlertCircle size={26} />
          </ThemeIcon>
        }
        title="Couldn't load events"
        description="Please check your connection and try again."
      >
        <Button
          variant="default"
          size="sm"
          mt="xs"
          loading={isFetching}
          onClick={() => refetch()}
        >
          Try again
        </Button>
      </StatusMessage>
    );
  } else if (ongoing.length === 0 && upcoming.length === 0) {
    content = (
      <StatusMessage
        icon={
          <ThemeIcon variant="light" radius="xl" size={48}>
            <IconCalendarOff size={26} />
          </ThemeIcon>
        }
        title="No upcoming events"
        description="There are no events scheduled right now. Check back later for new events."
      />
    );
  } else {
    content = (
      <Stack gap="xl">
        {ongoing.length > 0 && (
          <WebinarSection title="Today">{renderCards(ongoing)}</WebinarSection>
        )}
        {upcoming.length > 0 && (
          <WebinarSection title="Upcoming">
            {renderCards(upcoming)}
          </WebinarSection>
        )}
      </Stack>
    );
  }

  return (
    <MantineProvider theme={theme}>
      <Box
        bg="gray.0"
        mih="100vh"
        px={{ base: "md", sm: "xl" }}
        py={{ base: "md", sm: "lg" }}
      >
        {content}
      </Box>
      <ShareWebinarModal
        event={selectedEvent}
        opened={activeModal === "share"}
        onClose={() => setActiveModal(null)}
        shareUrl={`${webinars?.frontendUrl ?? DEFAULT_FRONTEND_URL}/webinar-detail/${selectedEvent?.identifier}`}
      />
      <JoinWebinarModal
        event={selectedEvent}
        opened={activeModal === "join"}
        onClose={() => setActiveModal(null)}
      />
    </MantineProvider>
  );
};

export default withClientNumber(WebinarLayout);
